import React, {ReactElement, ReactNode, useEffect, useRef} from 'react';
import {connectHandlers, disconnectHandlers, getTranslateX, setTranslateX} from 'ui/utils/dom';
import {Swipable} from './style';

type SwipeDirection = 'horizontal' | 'vertical';

function getEventMovementComponent(direction: SwipeDirection): (e: any) => number {
  const componentName = direction === 'horizontal' ? 'clientX' : 'clientY';
  return function getComponentValue(e: any): number {
    return (
      (e.changedTouches && e.changedTouches.length && e.changedTouches[0][componentName]) ||
      (e.touches && e.touches.length && e.touches[0][componentName]) ||
      e[componentName] ||
      0
    );
  };
}

const getEventX = getEventMovementComponent('horizontal');
const getEventY = getEventMovementComponent('vertical');

interface SwipeProviderProps {
  children: ReactNode;
  maxDisplacement: number;
  autoCompleteTranslation?: boolean;
}

export const SwipeProvider = ({
  children,
  maxDisplacement,
  autoCompleteTranslation = false,
}: SwipeProviderProps): ReactElement => {
  const threshold = maxDisplacement / 2;
  const swipableRef = useRef();
  const node: Maybe<HTMLElement> = swipableRef.current;

  useEffect(() => {
    let shift = 0;
    let pointerDown = false;
    let startPositionX = 0;
    let startPositionY = 0;

    function calcShift(e: NativePointerEvent): number {
      return Math.max(Math.min(getEventX(e) - startPositionX, 0), -1 * maxDisplacement);
    }

    function swipeDirection(e: NativePointerEvent): SwipeDirection {
      const horizontalComponent = Math.abs(getEventX(e) - startPositionX);
      const verticalComponent = Math.abs(getEventY(e) - startPositionY);
      return horizontalComponent - verticalComponent > 5 ? 'horizontal' : 'vertical';
    }

    function completeTranslation(element: HTMLElement): void {
      const finalPosition = Math.abs(shift) < threshold ? 0 : -1 * maxDisplacement;
      setTranslateX(element, finalPosition);
    }

    function startHandler(e: NativePointerEvent): void {
      pointerDown = true;
      startPositionY = getEventY(e);
      startPositionX = getEventX(e) - getTranslateX(node);
      node.classList.add('no-transition');
    }

    function moveHandler(e: NativePointerEvent): void {
      if (!pointerDown || swipeDirection(e) !== 'horizontal') return;
      e.preventDefault();
      shift = calcShift(e);
      setTranslateX(node, shift);
    }

    function endHandler(e: NativePointerEvent): void {
      if (!pointerDown) return;
      pointerDown = false;
      shift = calcShift(e);
      node.classList.remove('no-transition');
      if (autoCompleteTranslation) completeTranslation(node);
    }

    if (node) {
      connectHandlers(node, ['mousedown', 'touchstart'], startHandler);
      connectHandlers(document.body, ['mousemove', 'touchmove'], moveHandler);
      connectHandlers(document.body, ['mouseup', 'touchend'], endHandler);
    }

    return () => {
      if (!node) return;
      disconnectHandlers(node, ['mousedown', 'touchstart'], startHandler);
      disconnectHandlers(document.body, ['mousemove', 'touchmove'], moveHandler);
      disconnectHandlers(document.body, ['mouseup', 'touchend'], endHandler);
    };
  });

  return (
    <Swipable data-test='swipable' ref={swipableRef}>
      {children}
    </Swipable>
  );
};
